<template>
  <!-- Animated Background (disabled on mobile/reduced motion) -->
  <div v-if="showAnimatedBackground" class="animated-background">
    <div class="glow glow-1"></div>
    <div class="glow glow-2"></div>
    <div class="glow glow-3"></div>
  </div>

  <!-- Hero Carousel Component -->
  <HeroCarousel />
  
  <!-- Brand Logos Section -->
  <section v-if="renderBelowFold" class="brands-section container-fluid d-lg-block d-none mb-0">
    <div class="container-fluid">
      <div class="brands-wrapper" 
           @mouseenter="pauseBrandsCarousel" 
           @mouseleave="resumeBrandsCarousel">
        <div class="brands-track" v-if="!brandsLoading" :class="{ 'paused': isPaused }">
          <div class="brands-grid">
            <div v-for="{ brand, keySuffix } in marqueeBrands" :key="brand.brand_id + keySuffix" class="brand-item">
              <router-link :to="{ path: '/productos', query: { bank: slugify(brand.brand_name) }}" class="brand-link">
                <img
                  :src="optimizeBrandImg(brand.image)"
                  :srcset="brandImgSrcset(brand.image) || undefined"
                  sizes="(min-width: 1200px) 200px, (min-width: 992px) 160px, 120px"
                  :alt="brand.brand_name"
                  loading="lazy"
                  decoding="async"
                  width="200"
                  height="120"
                />
              </router-link>
            </div>
          </div>
        </div>
        <!-- Skeleton loader for brands -->
        <div class="brands-track" v-else>
          <div class="brands-grid">
            <div v-for="n in 10" :key="'skeleton-brand-' + n" class="brand-item">
              <div class="skeleton-brand"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Ofertas Section - Mostrar primero si hay productos con descuento -->
  <section class="catalog px-4 pt-5 d-flex align-items-center" v-if="renderBelowFold && (isLoading || productsOnSale.length > 0)">
    <div class="container pt-5">
      <div class="catalog__header mb-3">
        <h2 class="catalog__title font-super display-1 text-white mb-3 fw-bold">
          Ofertas<span style="color: var(--primary-color)">.</span>
        </h2>
        <p class="catalog__subtitle text-gray col-lg-7">
          Descubre las últimas ofertas en semillas que tenemos para vos.
        </p>
      </div>
      <div class="d-flex justify-content-start mb-4">
        <a
          class="btn btn-primary me-2 fs-lg-6 btn-lg py-2 px-4"
          href="/productos"
        >
          <i class="fas fa-store me-2" aria-hidden="true"></i>
          Ver todas
        </a>
      </div>
      <div class="catalog__products">
        <ProductsCarousel :products="productsOnSale" :isLoading="isLoading" carouselType="offers" class="mt-0"/>
      </div>
    </div>
  </section>

  <!-- Últimos ingresos Section - Mostrar si no hay ofertas o después de las ofertas -->
  <section class="catalog px-4 pt-5 d-flex align-items-center" v-if="renderBelowFold && !isLoading && productsOnSale.length === 0">
    <div class="container pt-5">
      <div class="catalog__header mb-3">
        <h2 class="catalog__title font-super display-1 text-white mb-3 fw-bold">
          Últimos ingresos<span style="color: var(--primary-color)">.</span>
        </h2>
        <p class="catalog__subtitle text-gray col-lg-7">
          Descubre las últimas variedades de semillas que ingresaron a nuestro
          catálogo.
        </p>
      </div>
      <div class="d-flex justify-content-start mb-4">
        <a
          class="btn btn-primary me-2 fs-lg-6 py-2 px-4"
          href="/productos"
        >
          <i class="fas fa-store me-2" aria-hidden="true"></i>
          Ver todas
        </a>
      </div>
      <div class="catalog__products">
        <ProductsCarousel :products="products" :isLoading="productsLoading" carouselType="latest" class="mt-0"/>
      </div>
    </div>
  </section>

  <!-- Genetics Section -->
  <section v-if="renderBelowFold" class="genetics container-fluid d-flex justify-content-center px-4">
    <div class="container">
      <div class="genetics__header mb-5">
        <h2 class="genetics__title font-super display-1 mb-3 fw-bold text-white">
          Las mejores genéticas<span style="color: var(--primary-color)"
            >.</span
          >
        </h2>
        <p class="genetics__subtitle text-gray fs-6">
          Elegí entre las mejores genéticas del mercado nacional e internacional
          en un solo lugar.
        </p>
      </div>

      <div class="row gy-4 mb-5">
        <div class="col-lg-6">
          <a
            href="/productos?category=automaticas"
            class="text-decoration-none card genetics-card h-100"
          >
            <div class="card-body p-3">
              <div class="card-image-wrapper feminized">
                <img
                  :src="geneticsGlue.src"
                  :srcset="geneticsGlue.srcset"
                  :sizes="geneticsGlue.sizes"
                  alt="Gorilla glue flower"
                  class="card-image feminized-image"
                  loading="lazy"
                  decoding="async"
                  width="160"
                  height="180"
                  style="max-width: 100%; height: auto;"
                />
              </div>
              <h3 class="card-title  mb-0 d-flex align-items-center justify-content-end">
                Semillas <br />
                Automáticas
              </h3>
            </div>
          </a>
        </div>
        <div class="col-lg-6 col-12 pe-lg-4">
          <a
            href="/productos?category=feminiizadas"
            class="text-decoration-none card genetics-card h-100"
          >
          <div class="card-image-wrapper seeds_box">
            <img
              :src="geneticsSeeds.src"
              :srcset="geneticsSeeds.srcset"
              :sizes="geneticsSeeds.sizes"
              alt="cannabis flower"
              class="card-image seeds_box-image"
              loading="lazy"
              decoding="async"
              width="260"
              height="260"
              style="max-width: 100%; height: auto;"
            />
          </div>
            <div class="card-body p-3 d-flex align-items-start">
              <h3 class="card-title  mb-0">
                Semillas <br />
                feminizadas
              </h3>
            </div>
          </a>
        </div>
        <div class="col-lg-5 col-12">
            <a
            href="/productos?category=regulares"
            class="text-decoration-none card genetics-card h-100"
            >
            <div class="card-body p-4 ps-0 position-relative h-100">
              <div class="position-absolute top-0 end-0 p-3">
              <h3 class="mb-0 card-title  text-end">
                Semillas <br />
                Regulares
              </h3>
              </div>
              <div class="position-absolute bottom-0 start-0">
              <img
                :src="geneticsLeaf.src"
                :srcset="geneticsLeaf.srcset"
                :sizes="geneticsLeaf.sizes"
                alt="cannabis flower"
                class="img-fluid col-lg-8 col-6"
                loading="lazy"
                decoding="async"
                width="260"
                height="260"
                style="max-width: 100%; height: auto;"
              />
              </div>
            </div>
            </a>
        </div>
        <div class="col-lg-7 col-12 pe-lg-4">
          <a
            href="/como-germinar"
            class="text-decoration-none card genetics-card h-100"
            style="overflow: hidden;"
          >
            <div class="card-body px-lg-4" style="z-index: 2;">
              <h3 class="card-title mb-3 ">
                Garantía de germinación
              </h3>
              <p class="text-muted small mb-0 col-lg-8 col-7">
                Nuestras semillas cuentan con garantía de germinación por parte
                del banco.
              </p>
            </div>
            <div class="position-absolute verified-icon">
              <img src="@/assets/icons/verified.svg" alt="check icon" class="img-fluid col-12 opacity-50" loading="lazy" decoding="async" width="300" height="300">
            </div>
          </a>
        </div>
      </div>
    </div>
  </section>

  <!-- Últimos ingresos Section - Mostrar después de las genéticas si hay ofertas -->
  <section class="catalog px-4 d-flex align-items-center" v-if="renderBelowFold && productsOnSale.length > 0">
    <div class="container">
      <div class="catalog__header mb-3 text-center">
        <h2 class="catalog__title font-super display-1 text-white mb-0 fw-bold">
          Últimos ingresos<span style="color: var(--primary-color)">.</span>
        </h2>
        <div class="d-flex justify-content-center">
          <p class="catalog__subtitle text-gray col-lg-7">
            Descubre las últimas variedades de semillas que ingresaron a nuestro
            catálogo.
          </p>
        </div>
      </div>
      <div class="d-flex justify-content-center mb-4">
        <a
          class="btn btn-primary me-2 fs-lg-6 py-2 px-4"
          href="/productos"
        >
          <i class="fas fa-store me-2" aria-hidden="true"></i>
          Ver todas
        </a>
      </div>
      <div class="catalog__products">
        <ProductsCarousel :products="products" :isLoading="productsLoading" carouselType="latest" class="mt-0"/>
      </div>
    </div>
  </section>

  <!-- FAQ Section -->
  <section v-if="renderBelowFold" class="faq-section container-fluid px-3" id="preguntas-frecuentes">
    <div class="d-flex justify-content-center mt-5 px-0">

      <!-- Content Container -->
      <div class="container position-relative row d-flex justify-content-center">
        <div class="col-5 d-lg-flex d-none align-items-start">
          <img src="@/assets/images/cogollo-violeta.webp" alt="" class="img-fluid col-3 floating-image floating-image-blurred" loading="lazy" decoding="async" width="200" height="300">
          <img src="@/assets/images/cogollo-violeta.webp" alt="" class="img-fluid col-7 floating-image" loading="lazy" decoding="async" width="200" height="300">
        </div>
        <div class="col-lg-6">

          <div class="faq-header mb-5">
            <h2 class="font-super display-1 text-white fw-bold mb-3">
              Preguntas Frecuentes<span class="text-primary">.</span>
            </h2>
            <p class="text-gray">
              Encuentra respuestas rápidas a las preguntas más comunes sobre
              nuestros productos y servicios.
            </p>
          </div>

          <div class="accordion custom-accordion" id="faqAccordion">
            <div
              class="accordion-item mb-3 font-super "
              v-for="(faq, index) in faqs"
              :key="index"
            >
              <h3 class="accordion-header" :id="'faqHeading' + index">
                <button
                  class="accordion-button collapsed font-super fs-5 fw-semibold"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="'#faq' + index"
                  :aria-controls="'faq' + index"
                  aria-expanded="false"
                >
                  <i :class="faq.icon" class="me-3 text-white" aria-hidden="true"></i>
                  {{ faq.question }}
                </button>
              </h3>
              <div
                :id="'faq' + index"
                class="accordion-collapse collapse"
                data-bs-parent="#faqAccordion"
                :aria-labelledby="'faqHeading' + index"
              >
                <div class="accordion-body fw-light">
                  {{ faq.answer }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { defineAsyncComponent } from "vue";
import HeroCarousel from "@/components/HeroCarousel.vue";
import { getProductsOnSale, getLatestProductsWithStock } from "@/services/products";
import { getBrands } from "@/services/brands";
import { optimizeCloudinaryUrl, cloudinarySrcset } from "@/utils/cloudinaryImage";

const ProductsCarousel = defineAsyncComponent({
  loader: () => import("@/components/ProductsCarousel.vue"),
  delay: 200,
  timeout: 15000,
});

export default {
  name: "PageHome",
  components: {
    HeroCarousel,
    ProductsCarousel,
  },
  data() {
    return {
      products: [],
      productsOnSale: [],
      productsLoading: true,
      isLoading: false,
      brandsLoading: true,
      renderBelowFold: false,
      showAnimatedBackground: false,
      faqs: [
        {
          icon: "fas fa-shipping-fast",
          question: "¿Realizan envíos a todo el país?",
          answer:
            "Sí, realizamos envíos a todo el país a través de diferentes servicios de mensajería.",
        },
        {
          icon: "fas fa-seedling",
          question: "¿Las semillas tienen garantía?",
          answer:
            "Sí, todas nuestras semillas cuentan con garantía de germinación.",
        },
        {
          icon: "fas fa-credit-card",
          question: "¿Qué métodos de pago aceptan?",
          answer:
            "Al confirmar el pedido se te indicará como proceder con el pago através de Whatsapp.",
        },
      ],
      brandsList: [],
      isPaused: false,
      geneticsGlue: {
        src: require('@/assets/images/glueauto-280.webp'),
        srcset: [
          `${require('@/assets/images/glueauto-240.webp')} 240w`,
          `${require('@/assets/images/glueauto-280.webp')} 280w`,
          `${require('@/assets/images/glueauto-360.webp')} 360w`,
          `${require('@/assets/images/glueauto-420.webp')} 420w`,
        ].join(', '),
        sizes: '(min-width: 992px) min(32vw, 380px), min(92vw, 340px)',
      },
      geneticsSeeds: {
        src: require('@/assets/images/seeds_box_to_left-480.webp'),
        srcset: [
          `${require('@/assets/images/seeds_box_to_left-360.webp')} 360w`,
          `${require('@/assets/images/seeds_box_to_left-480.webp')} 480w`,
          `${require('@/assets/images/seeds_box_to_left-560.webp')} 560w`,
          `${require('@/assets/images/seeds_box_to_left-640.webp')} 640w`,
          `${require('@/assets/images/seeds_box_to_left-720.webp')} 720w`,
        ].join(', '),
        sizes: '(min-width: 992px) min(42vw, 560px), min(94vw, 480px)',
      },
      geneticsLeaf: {
        src: require('@/assets/images/cannabis-leaf-360.webp'),
        srcset: [
          `${require('@/assets/images/cannabis-leaf-240.webp')} 240w`,
          `${require('@/assets/images/cannabis-leaf-280.webp')} 280w`,
          `${require('@/assets/images/cannabis-leaf-360.webp')} 360w`,
          `${require('@/assets/images/cannabis-leaf-400.webp')} 400w`,
        ].join(', '),
        sizes: '(min-width: 992px) min(42vw, 440px), min(90vw, 380px)',
      },
    };
  },
  computed: {
    filteredBrands() {
      return this.brandsList.filter(brand => 
        brand.image !== 'https://res.cloudinary.com/dkpmp8fwq/image/upload/v1748839616/logo.c7d24e78_rdvx4o.png'
      );
    },
    marqueeBrands() {
      const list = this.filteredBrands;
      return [
        ...list.map((b) => ({ brand: b, keySuffix: '-a' })),
        ...list.map((b) => ({ brand: b, keySuffix: '-b' })),
      ];
    },
  },
  methods: {
    // Add new slugify method to format brand names for URLs
    slugify(text) {
      return text
        ?.toString()
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/[\s-]+/g, '-');
    },
    async loadProducts() {
      try {
        this.productsLoading = true;
        // Usar la nueva función que obtiene productos más recientes con stock y precio
        const response = await getLatestProductsWithStock(8);
        // getLatestProductsWithStock devuelve un objeto con results
        const data = response.results || response;
        this.products = data;
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        this.productsLoading = false;
      }
    },
    async loadProductsOnSale() {
      try {
        this.isLoading = true;
        const response = await getProductsOnSale();
        // Acceder a results que contiene el array de productos
        this.productsOnSale = response.results || [];
      } catch (error) {
        console.error('Error loading products on sale:', error);
        this.productsOnSale = []; // Asignar array vacío en caso de error
      } finally {
        this.isLoading = false;
      }
    },
    async loadBrands() {
      try {
        this.brandsLoading = true;
        const brands = await getBrands();
        this.brandsList = brands;
      } catch (error) {
        console.error('Error loading brands:', error);
        this.brandsList = [];
      } finally {
        this.brandsLoading = false;
      }
    },
    pauseBrandsCarousel() {
      this.isPaused = true;
    },
    resumeBrandsCarousel() {
      this.isPaused = false;
    },
    optimizeBrandImg(url) {
      return optimizeCloudinaryUrl(url, { maxWidth: 200, quality: 'eco' });
    },
    brandImgSrcset(url) {
      return cloudinarySrcset(url, [80, 120, 160, 200], { quality: 'eco' });
    },
    getIdleScheduler() {
      if (typeof requestIdleCallback !== "undefined") {
        return (fn) => requestIdleCallback(fn, { timeout: 2500 });
      }
      return (fn) => setTimeout(fn, 300);
    },
    initPerfFlags() {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const isMobile = window.matchMedia("(max-width: 768px)").matches;
      this.showAnimatedBackground = !reducedMotion && !isMobile;
    },
  },
  async mounted() {
    this.initPerfFlags();
    const schedule = this.getIdleScheduler();
    schedule(() => {
      this.renderBelowFold = true;
      this.loadProducts();
      this.loadBrands();
      this.loadProductsOnSale();
    });
  },
};
</script>

<style scoped>
.catalog{
  background: transparent;
  padding: 0 0 3rem 0;
  min-height: 60vh;
  position: relative;
  content-visibility: auto;
  contain-intrinsic-size: 800px;
}

.gradient_title {
  background: linear-gradient(
    90deg,
    var(--primary-color),
    var(--tertiary-color)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.brands-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(to right, transparent, var(--primary-color), transparent);
}

.genetics {
  background-color: transparent;
  padding: 3rem 0;
  min-height: 60vh;
  position: relative;
  content-visibility: auto;
  contain-intrinsic-size: 900px;
}

.genetics-card {
  border-radius: 16px;
  border: none;
  overflow: visible;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  background-color: #fff;
}
.genetics img {
  opacity: 1;
}
@media (hover: hover) {
  .genetics-card:hover, .genetics-card img:hover {
    filter: grayscale(0%);
  }
  .genetics-card:hover {
    transform: translateY(-5px);
  }
}
.card {
  min-height: 220px;
}

.card-title {
  color: var(--black-color);
  position: relative;
  z-index: 2;
  font-size: 2rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 1000 !important;
  text-transform: uppercase;
  letter-spacing: -1px;
}

.card-image-wrapper {
  position: absolute;
  overflow: visible;
}

.card-image-wrapper.feminized {
  bottom: 12%;
  left: 5%;
  width: 180px;
}

.card-image-wrapper.seeds_box {
  top: -20%;
  right: 0;
  width: 300px;
  height: 300px;
}
.verified-icon{
  width: 300px; bottom: -100px; right: -80px; z-index: 1;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.feminized-image {
  transform: scale(1.2);
}
.seeds_box-image {
  width: 100%;
}

.heart-icon i {
  color: white;
  font-size: 1.5rem;
}
.floating-image{
  animation: floating 3s infinite;
  will-change: transform;
}
.floating-image-blurred{
  filter: blur(2px);
  opacity: 0.5;
}
@keyframes floating {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  } 
  100% {
    transform: translateY(0);
  }
}

@media (max-width: 991.98px) {
  .card-image-wrapper.feminized {
    bottom: 13%;
    right: 0;
    width: 200px;
  }
  .card-image-wrapper.seeds_box {
    top: auto;
    bottom: -10%;
    right: 0;
    width: 285px;
    height: 285px;
  }
}

.faq-section {
  background: transparent;
  position: relative;
  overflow: hidden;
  min-height: 0;
  content-visibility: auto;
  contain-intrinsic-size: 900px;
}

.accordion{
  width: 100%;
  min-height: 400px;
}

.flower-image {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 40%;
  z-index: 1;
}

.flower-image img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.container {
  z-index: 2;
}

.custom-accordion .accordion-item {
  background: var(--black-color-3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.custom-accordion .accordion-button {
  background: var(--black-color-3);
  color: var(--white-color);
  border: none;
  padding: 1.25rem;
  font-weight: 500;
}

.custom-accordion .accordion-button:not(.collapsed) {
  background: var(--primary-color);
  color: white;
}

.custom-accordion .accordion-button::after {
  filter: invert(1);
}

.custom-accordion .accordion-body {
  background: var(--black-color-2);
  color: var(--white-color-2);
  padding: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.accordion-button:hover {
  background: rgba(255, 255, 255, 0.05);
}

.accordion-button:focus {
  box-shadow: none;
  border-color: var(--primary-color);
}

@media (max-width: 991.98px) {
  .card-title{
    font-size: 2.5rem;
  }
  .flower-image {
    width: 60%;
    opacity: 0.2;
  }
  .faq-section {
    min-height: 0;
  }
}
@media (max-width: 500px) {
  .card-title{
    font-size: 2rem;
  }
  .card-image-wrapper.feminized {
  bottom: 12%;
  right: -5%;
  width: 170px;
}

.card-image-wrapper.seeds_box {
  bottom: -10%;
  right: 0;
  width: 260px;
  height: 260px;
}
.card-image-wrapper.leaf {
  width: 80%;
}
.verified-icon{
  width: 200px; bottom: -50px; right: -50px; z-index: 1;
}
}

@media (max-width: 400px) {
  .card-title{
    font-size: 1.5rem !important;
    letter-spacing: 0.001rem;
  }
  .card-image-wrapper.feminized {
  bottom: 12%;
  right: 0% ;
  width: 170px;
}
.card-image-wrapper.seeds_box {
  bottom: -10%;
  right: 0;
  width: 240px;
  height: 240px;
}
.verified-icon{
  width: 150px; bottom: -10px; right: -40px; z-index: 1;
}
}

/* Actualizar los estilos de la sección de brands */
.brands-section {
    background: transparent;
    padding: 3rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 0;
  overflow: hidden;
  min-height: 0;
  content-visibility: auto;
  contain-intrinsic-size: 500px;
}

.brands-grid {
  display: flex;
  gap: 2rem;
  padding: 0 4rem;
  align-items: center;
}

.brand-item {
  flex: 0 0 auto;
  width: 220px;
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: grayscale(100%) brightness(200%);
  opacity: 0.5;
  transition: all 0.3s ease;
  padding: 1rem;
  max-width: 200px; /* Asegurar que los logos no sean demasiado grandes */
}

.brand-item img:hover {
  filter: grayscale(0) brightness(100%);
  opacity: 1;
}

.brand-link {
  display: block;
  width: 100%;
  height: 100%;
  cursor: pointer;
  transition: all 0.2s ease;
}

.brand-link:hover img {
  filter: grayscale(0) brightness(100%);
  opacity: 1;
  transform: scale(1.05);
}

/* Ajustar responsive para tablets y móviles */
@media (max-width: 991px) {
  .brands-grid {
    gap: 5rem;
    padding: 0 2rem;
  }
  
  .brand-item {
    width: 120px;
    height: 70px;
  }
}

@media (max-width: 768px) {
  .brands-grid {
    gap: 4rem;
    padding: 0 1.5rem;
  }
  
  .brand-item {
    width: 100px;
    height: 60px;
  }
}

@media (max-width: 576px) {
  .brands-grid {
    gap: 3.5rem;
    padding: 0 1rem;
  }
  
  .brand-item {
    width: 80px;
    height: 50px;
  }
}

.brands-wrapper {
  width: 100%;
  overflow: hidden;
  position: relative;
}

.brands-track {
  display: flex;
  width: fit-content;
  animation: slideLogos 30s linear infinite;
}

.brands-track.paused {
  animation-play-state: paused;
}

@keyframes slideLogos {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* Skeleton Loaders */
.skeleton-brand {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
}

@keyframes loading {
  to {
    background-position: -200% 0;
  }
}

/* Animated Background */
.animated-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.35;
  animation: float 20s ease-in-out infinite;
}

.glow-1 {
  width: 800px;
  height: 800px;
  background: radial-gradient(circle, #512861 10%, transparent 100%);
  top: -20%;
  right: -10%;
  animation-delay: 0s;
}

.glow-2 {
  width: 700px;
  height: 700px;
  background: radial-gradient(circle, #3b1d47 0%, transparent 70%);
  bottom: 10%;
  left: -15%;
  animation-delay: -7s;
}

.glow-3 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #361d41 0%, transparent 70%);
  top: 40%;
  right: 10%;
  animation-delay: -14s;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(30px, -30px) scale(1.1);
  }
  50% {
    transform: translate(-20px, 20px) scale(0.95);
  }
  75% {
    transform: translate(-30px, -20px) scale(1.05);
  }
}

/* Línea de gradiente sutil */
.catalog::before,
.genetics::before,
.faq-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(155, 89, 182, 0.3), transparent);
  opacity: 0.5;
}

@media (max-width: 768px) {
  .glow-1 {
    width: 300px;
    height: 300px;
  }
  .glow-2 {
    width: 250px;
    height: 250px;
  }
  .glow-3 {
    width: 200px;
    height: 200px;
  }
  .glow {
    filter: blur(40px);
    opacity: 0.08;
  }
}

@media (max-width: 768px) {
  .floating-image,
  .floating-image-blurred {
    animation: none;
    filter: none;
  }
  .brands-track {
    animation: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .animated-background,
  .glow,
  .floating-image,
  .floating-image-blurred,
  .brands-track {
    animation: none !important;
    transition: none !important;
  }
}
</style>
